import React, { useState } from 'react';
import { Bubble, Segment } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import CardBox from '@/pages/dashboard/components/CardBox';
import MiniTable from '@/pages/dashboard/components/MiniTable';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { formatDataByChartType, xAxisHighLight, xAxisLabelFormat } from '@/pages/dashboard/components/Chart/_config/plug';
import { handleDownload } from '@/pages/dashboard/utils/utils';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { ChurnChartCoProps } from './type';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';

const defaultHideConfig = {
  eyes: false,
  barChart: false,
  lineChart: false,
  sheet: false
};

const ChurnChartCo = (props : ChurnChartCoProps) => {
  const { 
    options, 
    timeType, 
    tabName, 
    setTabName, 
    tabList = [], 
    title, 
    titleDes = [], 
    isLoading, 
    isError, 
    isNoData, 
    defaultActive,
    setOtherConfigFunc, 
    formatterFunc, 
    isEye, 
    setIsEye, 
    hideConfig = defaultHideConfig,
    height,
    filterSlot,
    handleAxisFormat: handleAxisFormatProps = undefined
  } = props;

  const { getLocalText } = useLocal();
  const [isHover, setIsHover] = useState(false);

  const barOptions = formatDataByChartType('bar', options?.chart);
  const lineOptions = formatDataByChartType('line', options?.chart);
  const tableOptions = formatDataByChartType('table', options?.table);

  const handleAxisFormat = (value: any) => timeType === "Month" ? dateTimeFormat(value,'YYYY-MM') : dateTimeFormat(value,'MM-DD');

  const renderList = [
    {
      type: 'eyes',
      svgType: isEye ? 'open-eyes' : 'eyes-closed',
      content: isEye ? getLocalText('common_show_values') : getLocalText('common_hide_values'),
      isHideCom: true,
    },
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: getLocalText('common_bar_chart'),
      component: (
        <BarChart
          height={height || 340}
          legendData={barOptions?.legend ?? []}
          xAxisData={barOptions?.xAxisData ?? []}
          series={barOptions?.series ?? []}
          formatterFunc={formatterFunc}
          setOtherConfigFunc={(opt: any) => {
            if(setOtherConfigFunc){
              opt = setOtherConfigFunc(opt, timeType);
            }

            if (typeof handleAxisFormatProps === 'function') {
              opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormatProps });  
              opt = xAxisHighLight(opt, {
                formatter: (params: { value: string; }) => handleAxisFormatProps(params.value)
              });
            } else if (typeof handleAxisFormat === 'function') {
              opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });      
              opt = xAxisHighLight(opt, {
                formatter: (params: { value: string; }) => handleAxisFormat(params.value)
              });
            }

            return opt;
          }}
        />
      )
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: getLocalText('common_line_chart'),
      component: (
        <LineChart
          height={height || 340}
          legendData={lineOptions?.legend ?? []}
          xAxisData={lineOptions?.xAxisData ?? []}
          series={lineOptions?.series ?? []}
          formatterFunc={formatterFunc}
          setOtherConfigFunc={(opt: any) => {
            if(setOtherConfigFunc){
              opt = setOtherConfigFunc(opt, timeType);
            }
            if (typeof handleAxisFormatProps === 'function') {
              opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormatProps });  
              opt = xAxisHighLight(opt, {
                formatter: (params: { value: string; }) => handleAxisFormatProps(params.value)
              });
            } else if (typeof handleAxisFormat === 'function') {
              opt = xAxisLabelFormat(opt, { xAxisLabelFormat: handleAxisFormat });      
              opt = xAxisHighLight(opt, {
                formatter: (params: { value: string; }) => handleAxisFormat(params.value)
              });
            }
            return opt;
          }}
        />
      )
    },
    {
      type: 'sheet',
      svgType: 'sheet',
      content: getLocalText('common_table'),
      component: (
        <MiniTable
          height={height || 340}
          loading={false}
          tableOption={tableOptions?.option}
          tableList={tableOptions?.data}
          // scrolLableVal={{
          //   virtualizedOptions: {
          //     height: 305,
          //     itemHeight: 50,
          //   },
          // }}
        />
      )
    },
  ];

  function filterRenderList(renderList: any[], hideConfig: any) {
    return renderList.filter((item) => !hideConfig[item.type]);
  }

  const filteredRenderList = filterRenderList(renderList, hideConfig);

  return (
    <CardBox
      className='register-table'
      height={height || 340}
      bordered={false}
      subTitleCom={
        tabList?.length > 0 ? 
          <Segment
            rimless={false}
            value={tabName}
            onChange={(value) => {
              setTabName(value);
            }}
            options={tabList}
          /> : null
      }
      titleCom={<>
        {title}
        {
          titleDes && titleDes?.length > 0 && (
            <Bubble
              className='register-table-tipWidth'
              dark
              content={
                <>
                  {
                    titleDes?.map((title: any, index: number) => (
                      <p
                        key={index}
                        dangerouslySetInnerHTML={{
                          __html: `
                              ${title?.title ? `${title?.title?.replace(/\\n/g, '<br/>')  }<br/>` : ''}
                              ${title.content?.replace(/\\n/g, '<br/>')}
                          `
                        }}
                        style={{
                          marginTop: index !== 0 ? '24px': '0'
                        }}
                      />
                    ))
                  }
                </>
              }
            >
              <i
                onMouseOver={()=>{setIsHover(true)}}
                onMouseOut={()=>{setIsHover(false)}}
                className='register-table-tip'
              >
                <SvgIcon
                  type={isHover?'alert-circle-action':'alert-circle-m'}
                  className='register-table-tipicon'
                />
              </i>
            </Bubble>
          )
        }
      </>}
      renderList={filteredRenderList}
      defaultAvtive={defaultActive}
      downloadClick={async () => {
        handleDownload(tableOptions, title);
      }}
      setCurrentDataType={(type : string) => {
        if (type === 'eyes') {
          setIsEye?.(!isEye);
        }
      }}
      isLoading={isLoading}
      isError={isError}
      isNodata={isNoData}
      filterSlot={filterSlot}
    />
  );
};

export default ChurnChartCo;